import { useState, useEffect, useCallback } from 'react';

interface InAppBrowserInfo {
  isInAppBrowser: boolean;
  browserType: 'facebook' | 'instagram' | 'messenger' | 'tiktok' | 'snapchat' | 'linkedin' | 'unknown' | null;
  platform: 'ios' | 'android' | 'unknown';
  canRedirect: boolean;
}

export const useInAppBrowser = (): InAppBrowserInfo & { openInExternalBrowser: () => void } => {
  const [info, setInfo] = useState<InAppBrowserInfo>({
    isInAppBrowser: false,
    browserType: null,
    platform: 'unknown',
    canRedirect: false,
  });

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera || '';
    const uaLower = ua.toLowerCase();

    // Detect platform
    let platform: 'ios' | 'android' | 'unknown' = 'unknown';
    if (/iphone|ipad|ipod/i.test(ua)) {
      platform = 'ios';
    } else if (/android/i.test(ua)) {
      platform = 'android';
    }

    // Detect in-app browsers - comprehensive detection
    let browserType: InAppBrowserInfo['browserType'] = null;
    let isInAppBrowser = false;

    // Facebook detection (FBAN = Facebook App, FBAV = Facebook Version)
    if (/FBAN|FBAV|FB_IAB|FBIOS|FBSS/i.test(ua)) {
      browserType = 'facebook';
      isInAppBrowser = true;
    }
    // Instagram detection
    else if (/Instagram/i.test(ua)) {
      browserType = 'instagram';
      isInAppBrowser = true;
    }
    // Messenger detection
    else if (/FBMESSENGER|Messenger/i.test(ua)) {
      browserType = 'messenger';
      isInAppBrowser = true;
    }
    // TikTok detection
    else if (/BytedanceWebview|TikTok/i.test(ua)) {
      browserType = 'tiktok';
      isInAppBrowser = true;
    }
    // Snapchat detection
    else if (/Snapchat/i.test(ua)) {
      browserType = 'snapchat';
      isInAppBrowser = true;
    }
    // LinkedIn detection
    else if (/LinkedInApp/i.test(ua)) {
      browserType = 'linkedin';
      isInAppBrowser = true;
    }
    // Generic WebView detection
    else if (
      uaLower.includes('wv') || 
      uaLower.includes('webview') ||
      (platform === 'android' && /Version\/[\d.]+.*Chrome\/[\d.]+/.test(ua)) ||
      (platform === 'ios' && !/Safari/i.test(ua) && /AppleWebKit/i.test(ua))
    ) {
      browserType = 'unknown';
      isInAppBrowser = true;
    }

    // Android can use intent:// URLs to open external browser
    const canRedirect = platform === 'android';

    setInfo({
      isInAppBrowser,
      browserType,
      platform,
      canRedirect,
    });
  }, []);

  const openInExternalBrowser = useCallback(() => {
    const currentUrl = window.location.href;
    
    if (info.platform === 'android') {
      // Intent URL for Android - opens in Chrome or default browser
      const intentUrl = `intent://${currentUrl.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`;
      
      // Try Chrome first, fallback to any browser
      try {
        window.location.href = intentUrl;
        
        // If intent fails after 2s, try generic browser intent
        setTimeout(() => {
          const genericIntent = `intent://${currentUrl.replace(/^https?:\/\//, '')}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
          window.location.href = genericIntent;
        }, 2000);
      } catch {
        // Final fallback: just copy URL suggestion
        console.log('Intent redirect failed');
      }
    } else if (info.platform === 'ios') {
      // iOS: Can try x-safari-https scheme but it's not reliable
      // Best UX is to show instructions
      try {
        // This rarely works but worth a try
        window.location.href = `x-safari-https://${currentUrl.replace(/^https?:\/\//, '')}`;
      } catch {
        console.log('Safari redirect not supported');
      }
    }
  }, [info.platform]);

  return {
    ...info,
    openInExternalBrowser,
  };
};

// Helper function to get friendly browser name
export const getBrowserName = (browserType: InAppBrowserInfo['browserType']): string => {
  switch (browserType) {
    case 'facebook':
      return 'Facebook';
    case 'instagram':
      return 'Instagram';
    case 'messenger':
      return 'Messenger';
    case 'tiktok':
      return 'TikTok';
    case 'snapchat':
      return 'Snapchat';
    case 'linkedin':
      return 'LinkedIn';
    default:
      return 'deze app';
  }
};
